import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleCanvasBreweriesVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-breweries-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "local-beer-culture": "Local Beer Culture & Styles",
    "brassneck-brewery": "Brassneck Brewery",
    "33-acres-brewing": "33 Acres Brewing",
    "parallel49-brewing": "Parallel 49 Brewing",
    "strange-fellows-brewing": "Strange Fellows Brewing",
    "main-street-brewing": "Main Street Brewing",
    "storm-brewing": "Storm Brewing",
    "granville-island-brewing": "Granville Island Brewing",
    "red-truck-beer-company": "Red Truck Beer Company",
    "plan-your-tour": "How to Plan Your Brewery Tour",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver",
    },
    {
      url: "https://bcaletrail.ca/",
      label: "BC Ale Trail",
    },
    {
      url: "https://vancouver.ca/",
      label: "City of Vancouver - Official Website",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location matches the brewery sections below. 
  // Note: The lat & lng must be EXACT with at least 5 decimals.
  const locations = [
    {
      id: "brassneck-brewery",
      lat: 49.26578,
      lng: -123.10055,
      popupText: "Brassneck Brewery (Mount Pleasant)",
    },
    {
      id: "33-acres-brewing",
      lat: 49.26400,
      lng: -123.10600,
      popupText: "33 Acres Brewing Company (Mt Pleasant)",
    },
    {
      id: "parallel49-brewing",
      lat: 49.28383,
      lng: -123.06436,
      popupText: "Parallel 49 Brewing Company",
    },
    {
      id: "strange-fellows-brewing",
      lat: 49.28200,
      lng: -123.07021,
      popupText: "Strange Fellows Brewing",
    },
    {
      id: "main-street-brewing",
      lat: 49.26468,
      lng: -123.09920,
      popupText: "Main Street Brewing Company",
    },
    {
      id: "storm-brewing",
      lat: 49.28200,
      lng: -123.07021, 
      popupText: "Storm Brewing Ltd.",
    },
    {
      id: "granville-island-brewing",
      lat: 49.27125,
      lng: -123.13400,
      popupText: "Granville Island Brewing",
    },
    {
      id: "red-truck-beer-company",
      lat: 49.26921,
      lng: -123.09870,
      popupText: "Red Truck Beer Company",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>The Ultimate Guide to Vancouver’s Best Breweries for Craft Beer Lovers</title>
        <meta
          name="description"
          content="Discover the top craft breweries in Vancouver, plus local beer styles, regulations, FAQs, and must-visit brewpubs across the city."
        />
        <meta
          name="keywords"
          content="Vancouver craft beer, breweries in Vancouver, best breweries, craft beer guide, local beer culture"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-breweries-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="The Ultimate Guide to Vancouver’s Best Breweries for Craft Beer Lovers"
        />
        <meta
          property="og:description"
          content="Explore Vancouver's vibrant craft beer scene with this comprehensive guide to the city's best breweries, local beer culture, and essential tips."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-breweries-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-breweries-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="The Ultimate Guide to Vancouver’s Best Breweries for Craft Beer Lovers"
        />
        <meta
          name="twitter:description"
          content="A detailed look at Vancouver's top craft breweries, local beer styles, and important regulations to know before you sip."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-breweries-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Vancouver's craft beer scene"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s craft beer scene is booming, with over 70 breweries scattered
              across the city. Whether you’re a seasoned beer aficionado or just curious
              about local flavors, Vancouver offers a deliciously diverse range of brews— 
              from crisp lagers to bold barrel-aged stouts. In popular neighborhoods like
              Mt. Pleasant’s “Brewery Creek” and East Vancouver’s “Yeast Van,” visitors
              can sample multiple breweries in a single afternoon. In this comprehensive
              guide, we’ll highlight Vancouver’s top craft breweries, share insights on
              local beer culture, and walk you through essential tips for a safe, 
              well-planned brewery-hopping adventure.
            </p>

            {/* SECTION: Local Beer Culture */}
            <h2 id="local-beer-culture">Local Beer Culture & Styles</h2>
            <p>
              Vancouver’s craft beer renaissance began in the 1980s with pioneers like
              Granville Island Brewing. Since then, the city has cultivated a strong
              culture of innovation and collaboration among brewers. You’ll find classic
              West Coast IPAs emphasizing piney, citrusy hops, as well as European-style
              sours, Belgian-inspired farmhouse ales, and experimental small-batch brews
              infused with unique flavors (think coffee, fruit, or even salted caramel).
            </p>
            <p>
              Tasting rooms often reflect the personality of each brewery—some opting
              for cozy, rustic charm, while others feature minimalist, modern designs.
              The common thread is a friendly, community-driven spirit: bartenders love
              to chat about new releases, and you’ll often bump into fellow beer lovers
              eager to swap tasting notes. As a visitor, you’ll find it easy to strike
              up conversations and learn more about B.C.’s flourishing craft scene.
            </p>

            {/* SECTION: Brassneck Brewery */}
            <h2 id="brassneck-brewery">Brassneck Brewery</h2>
            <p
              ref={(el) => setSpotRef("brassneck-brewery", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Brassneck Brewery (49.26578, -123.10055)</strong>{" "}
              <Marker
                coordY={49.26578}
                coordX={-123.10055}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A beloved neighborhood spot in Mount Pleasant, Brassneck opened its
              doors in 2013 and quickly cemented itself as a key player in Vancouver’s
              modern craft beer boom. The taproom is small yet vibrant, often buzzing
              with locals who come for the cozy ambience and the rotating selection
              of innovative, small-batch brews.
            </p>
            <p>
              <em>What to Expect:</em> Brassneck’s fresh beer lineup changes constantly,
              so each visit can offer new surprises. You might find a classic West Coast
              Pale Ale next to a unique barrel-aged sour, or a seasonal farmhouse ale 
              made with local ingredients. Seating is communal, fostering a convivial
              atmosphere. Brassneck doesn’t serve meals but often hosts rotating food
              trucks, and you’re welcome to bring your own snacks.
            </p>
            <p>
              <em>Address:</em> 2148 Main Street, Vancouver, BC V5T 3C5<br/>
              <em>Hours:</em> Mon–Fri: 2:00pm–11:00pm; Sat–Sun: 12:00pm–11:00pm<br/>
              <em>Approx. Cost:</em> Flights: ~C$9–12; Pints: ~C$7–8
            </p>

            {/* SECTION: 33 Acres Brewing */}
            <h2 id="33-acres-brewing">33 Acres Brewing Company</h2>
            <p
              ref={(el) => setSpotRef("33-acres-brewing", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>33 Acres Brewing (49.26400, -123.10600)</strong>{" "}
              <Marker
                coordY={49.26400}
                coordX={-123.10600}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Famous for its minimalist interior design and inviting ambiance,
              33 Acres combines a sleek café vibe in the mornings (yes, they serve
              coffee and light breakfast) with a bustling taproom by afternoon and
              evening. Their core lineup includes approachable favorites like 33 Acres
              of Ocean Pale Ale, along with experimental specials next door at the
              “33 Brewing Experiment.”
            </p>
            <p>
              <em>What to Expect:</em> The bright, white-walled tasting room is 
              an Instagrammer’s dream. You can sip a meticulously crafted flat white
              before noon, then switch to a crisp lager or a floral saison later in the
              day. Families often come by for weekend brunch, while craft enthusiasts
              drop in to chat about rotating seasonal releases. The space can fill up
              quickly on weekends, so arrive early if you prefer a quieter experience.
            </p>
            <p>
              <em>Address:</em> 15 W 8th Ave, Vancouver, BC V5Y 1M8<br/>
              <em>Hours:</em> Mon–Fri: 9:00am–11:00pm; Sat–Sun: 10:00am–11:00pm<br/>
              <em>Approx. Cost:</em> Pints: ~C$7; Flights: ~C$12; Light food items: C$5–15
            </p>

            {/* SECTION: Parallel 49 Brewing */}
            <h2 id="parallel49-brewing">Parallel 49 Brewing Company</h2>
            <p
              ref={(el) => setSpotRef("parallel49-brewing", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Parallel 49 Brewing (49.28383, -123.06436)</strong>{" "}
              <Marker
                coordY={49.28383}
                coordX={-123.06436}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located in East Vancouver, Parallel 49 is a heavyweight in the city’s
              craft beer scene. Known for imaginative branding and a quirky tasting
              room anchored by a faux indoor food truck, this brewery produces year-round
              staples (like the Filthy Dirty IPA or Salty Scot Scotch Ale) and seasonal
              experiments.
            </p>
            <p>
              <em>What to Expect:</em> With over 40 taps, Parallel 49 offers a broad 
              range—from hop-forward IPAs to dessert-inspired stouts. The large indoor
              “Street Kitchen” serves tacos, burgers, and other casual bites. Families
              are welcome in the daytime, while evenings bring an upbeat crowd. The
              whimsical label art and frequent limited-edition can releases keep fans
              returning for something new.
            </p>
            <p>
              <em>Address:</em> 1950 Triumph St, Vancouver, BC V5L 1K5<br/>
              <em>Hours:</em> Daily ~11:00am–11:00pm (Fri/Sat may be later)<br/>
              <em>Approx. Cost:</em> Pints: ~C$7.50; Flights: ~C$10
            </p>

            {/* SECTION: Strange Fellows Brewing */}
            <h2 id="strange-fellows-brewing">Strange Fellows Brewing</h2>
            <p
              ref={(el) => setSpotRef("strange-fellows-brewing", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Strange Fellows (49.28200, -123.07021)</strong>{" "}
              <Marker
                coordY={49.28200}
                coordX={-123.07021}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Strange Fellows expertly blends Old World brewing traditions with
              West Coast innovation. You’ll find Belgian-inspired sours, barrel-aged
              specialties, and unique seasonal ales. The tasting room’s modern design
              complements its slightly offbeat branding—plus, it houses the Charles
              Clark Gallery, a micro-art gallery.
            </p>
            <p>
              <em>What to Expect:</em> This spot is perfect for adventurous beer drinkers
              who enjoy sours, barrel-aged beers, and complex flavor profiles. 
              Each month may feature a new sour release or experimental farmhouse ale. 
              The brewery also hosts small-scale art events, capturing a creative local
              vibe. Seating is generally first-come, first-served, and the atmosphere
              leans relaxed and conversation-friendly.
            </p>
            <p>
              <em>Address:</em> 1345 Clark Dr, Vancouver, BC V5L 3K9<br/>
              <em>Hours:</em> ~12:00pm–10:00pm; extended Fri–Sat<br/>
              <em>Approx. Cost:</em> Pints: ~C$7–8; Flights: ~C$12
            </p>

            {/* SECTION: Main Street Brewing */}
            <h2 id="main-street-brewing">Main Street Brewing Company</h2>
            <p
              ref={(el) => setSpotRef("main-street-brewing", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Main Street Brewing (49.26468, -123.09920)</strong>{" "}
              <Marker
                coordY={49.26468}
                coordX={-123.09920}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Occupying a heritage building from 1913, Main Street Brewing exudes
              historical charm. Located in the Brewery Creek area, it offers a core
              selection of beers (like the popular Naked Fox IPA) and rotates through
              over 30 seasonals each year, providing plenty of variety.
            </p>
            <p>
              <em>What to Expect:</em> High wooden ceilings and exposed brick walls
              give this spot a distinct vintage vibe. There’s a modest food menu (think
              pretzels, bratwurst, nachos) to pair with your flight. Because of its
              central location, Main Street Brewing is often a go-to starting point for
              those exploring the nearby craft breweries in Mt. Pleasant.
            </p>
            <p>
              <em>Address:</em> 261 E 7th Ave, Vancouver, BC V5T 0B8<br/>
              <em>Hours:</em> Mon–Thu: 12:00pm–10:00pm; Fri–Sat: 12:00pm–11:00pm; Sun: 12:00pm–9:00pm<br/>
              <em>Approx. Cost:</em> Pints: ~C$7; Flights: ~C$10
            </p>

            {/* SECTION: Storm Brewing */}
            <h2 id="storm-brewing">Storm Brewing Ltd.</h2>
            <p
              ref={(el) => setSpotRef("storm-brewing", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Storm Brewing (49.28200, -123.07021)</strong>{" "}
              <Marker
                coordY={49.28200}
                coordX={-123.07021}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Established in 1994, Storm is Vancouver’s oldest independent craft
              brewery, often described as the city’s original “dive brewery.” The 
              industrial, no-frills warehouse tasting area is packed with stainless 
              steel tanks, and the staff are known for their creativity and friendly 
              vibe. 
            </p>
            <p>
              <em>What to Expect:</em> Storm’s “Brainstorms” are weekly limited-edition 
              experiments—past flavors include pickle beer, cotton candy ale, and more
              unorthodox creations. There’s no typical bar seating; you stand among 
              the vats and equipment. Tasters are “by donation,” and you can fill a
              growler to-go if a particular brew intrigues you. Bring cash for tips
              and donations, and don’t expect a glamorous taproom—this is all about 
              raw authenticity.
            </p>
            <p>
              <em>Address:</em> 310 Commercial Dr, Vancouver, BC V5L 3V6<br/>
              <em>Hours:</em> Typically 12:00pm–6:00pm; sometimes later Fri–Sat<br/>
              <em>Approx. Cost:</em> Tasters by donation; Growlers ~C$11–13
            </p>

            {/* SECTION: Granville Island Brewing */}
            <h2 id="granville-island-brewing">Granville Island Brewing</h2>
            <p
              ref={(el) => setSpotRef("granville-island-brewing", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Granville Island Brewing (49.27125, -123.13400)</strong>{" "}
              <Marker
                coordY={49.27125}
                coordX={-123.13400}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Canada’s first microbrewery, Granville Island Brewing, opened in 1984.
              Though it has grown significantly, the original small-batch taproom under
              the Granville Bridge remains a prime stop for tourists exploring the
              artisan shops and markets of Granville Island.
            </p>
            <p>
              <em>What to Expect:</em> Try familiar flagships like English Bay Pale Ale
              or Island Lager alongside limited “Taproom Series” releases. The cozy
              interior doubles as a mini brewing museum, featuring photos and history of
              the early days of Vancouver’s craft movement. Guided tours are available,
              often including tasting flights that introduce both novices and connoisseurs
              to classic West Coast styles.
            </p>
            <p>
              <em>Address:</em> 1441 Cartwright St, Vancouver, BC V6H 3R7<br/>
              <em>Hours:</em> ~11:00am–8:00pm (Sun–Thu); ~11:00am–9:00pm (Fri–Sat)<br/>
              <em>Approx. Cost:</em> Flights: ~C$9; Pints: ~C$7.50; Tour: ~C$12
            </p>

            {/* SECTION: Red Truck Beer Company */}
            <h2 id="red-truck-beer-company">Red Truck Beer Company</h2>
            <p
              ref={(el) => setSpotRef("red-truck-beer-company", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Red Truck Beer Company (49.26921, -123.09870)</strong>{" "}
              <Marker
                coordY={49.26921}
                coordX={-123.09870}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Featuring a retro truck-stop aesthetic, Red Truck offers a full 
              restaurant, lively patio, and frequent summer concerts. Core beers like 
              the Red Truck Lager and IPA cater to a wide audience, while rotating taps 
              push the envelope with fruit-infused or specialty seasonal brews.
            </p>
            <p>
              <em>What to Expect:</em> There’s ample seating, making Red Truck a great 
              spot for groups. The menu includes diner-style burgers, salads, and 
              weekend brunch. The “Truck Stop Concert Series” can transform the lot 
              into an outdoor music venue featuring local artists. Located near Olympic 
              Village, it’s easily accessible via transit or on foot if you’re brewery 
              hopping around Mt. Pleasant.
            </p>
            <p>
              <em>Address:</em> 295 E 1st Ave, Vancouver, BC V5T 1A7<br/>
              <em>Hours:</em> Sun–Wed: 11:00am–10:00pm; Thu: 11:00am–11:00pm; Fri–Sat: 11:00am–12:00am<br/>
              <em>Approx. Cost:</em> Pints: ~C$7; Flights: ~C$9; Entrees: C$12–18
            </p>

            {/* SECTION: Plan Your Tour */}
            <h2 id="plan-your-tour">How to Plan Your Brewery Tour</h2>
            <p>
              With so many breweries to choose from, organizing your ideal tasting route 
              is half the fun. Here are some practical tips:
            </p>
            <ul>
              <li>
                <strong>Cluster Your Visits:</strong> Brewery-rich zones like
                Mt. Pleasant (Main Street area) or East Van (around Hastings or
                Commercial) allow you to hit multiple spots on foot.
              </li>
              <li>
                <strong>Consider Transit:</strong> SkyTrain stations (e.g., Main Street–Science World,
                VCC–Clark) and frequent bus routes make it easy to hop between neighborhoods
                without worrying about parking or driving. Rideshares (Uber, Lyft) are widely available.
              </li>
              <li>
                <strong>Stay Hydrated & Fed:</strong> Many breweries offer food options (snacks, food trucks,
                or full restaurants). When in doubt, bring water or order a non-alcoholic beverage
                between pints. Sharing tasting flights is also a good way to pace yourself.
              </li>
              <li>
                <strong>Check Brewery Hours:</strong> Each brewery has unique operating times. Some open
                earlier (especially those that serve coffee or lunch), while others stay open later.
                Always verify online or call ahead to avoid disappointment.
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What is the legal drinking age in Vancouver?</h3>
              <p>
                The legal drinking age in British Columbia (including Vancouver) is
                <strong> 19 years old</strong>. Expect ID checks if you appear under 25.
              </p>

              <h3>2. Can I drink beer in public spaces?</h3>
              <p>
                Public consumption of alcohol is generally illegal in Vancouver unless
                you are in a specifically designated zone (certain pilot projects in 
                select parks). Otherwise, open alcohol on streets or beaches can lead 
                to fines.
              </p>

              <h3>3. How much does craft beer cost in Vancouver?</h3>
              <p>
                A typical pint is around C$7–9, flights range from ~C$8–12. Higher-ABV 
                or specialty beers may be slightly more expensive. Tipping (15–20%) is 
                customary for table or bar service.
              </p>

              <h3>4. Can I bring children to the breweries?</h3>
              <p>
                Some breweries, especially those with a food-primary license (like Red Truck
                or 33 Acres), allow minors during daytime or until a certain hour. However,
                smaller taproom-only breweries may be 19+ only. Check each brewery’s policy.
              </p>

              <h3>5. What are the best ways to get around safely?</h3>
              <p>
                Public transit, taxis, and rideshares are your friends. If you’re planning 
                a full day of tasting, consider an organized brewery tour, which provides 
                transport between breweries. Avoid driving if you plan to drink.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              The City of Vancouver enforces strict regulations around alcohol:
            </p>
            <ul>
              <li>
                <strong>Legal Drinking Age:</strong> 19+ only, with valid ID required at licensed
                establishments. Staff may refuse service if you appear intoxicated or do not have 
                proper identification.
              </li>
              <li>
                <strong>Open Liquor in Public:</strong> Illegal in most areas, leading to potential
                fines. Keep any purchased beer sealed until you’re in a private place or a licensed 
                venue.
              </li>
              <li>
                <strong>Impaired Driving:</strong> Strictly enforced by local authorities. Plan your
                transportation ahead—use transit, rideshare, or designated drivers.
              </li>
              <li>
                <strong>Food & Water:</strong> Many breweries encourage staying hydrated and ordering 
                food if you plan to drink multiple samples. Some offer free water stations or have 
                snacks onsite.
              </li>
            </ul>
            <p>
              Always check brewery websites for current hours, capacity limits, or any special
              instructions. Policies can change, so a quick online check helps you avoid surprises.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s thriving craft beer scene combines innovation, community spirit,
              and a deep respect for local tradition. From the heritage charm of Main Street 
              Brewing to the creative eccentricity of Storm Brewing, there’s a brewery here 
              for every taste and style. By planning your route thoughtfully and following local
              regulations, you can safely explore the city’s renowned “Brewery Creek” and 
              “Yeast Van” neighborhoods—plus plenty of hidden gems in between. Raise a glass
              to Vancouver’s dynamic beer culture and enjoy discovering the city, one taster 
              flight at a time!
            </p>
            <p>
              <em>Disclaimer:</em> Always verify operating hours, age restrictions, and local
              regulations with official resources. Drink responsibly, use public
              transportation or rideshare services, and respect the rules set by each
              establishment. This information is provided as a general guide and may
              change over time.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}